import React, { useEffect, useRef, useState } from 'react';
import { StatusBadge } from './ui/Primitives';
import './CallHistoryItem.css';

export function formatCallDuration(value) {
  const seconds = Math.max(Number(value) || 0, 0);
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  return `${minutes}:${String(remainder).padStart(2, '0')}`;
}

export function formatCallTime(value) {
  if (!value) return 'Processing';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Processing';
  return new Intl.DateTimeFormat(undefined, {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  }).format(date);
}

function normalizeCallStatus(status) {
  return String(status || '').trim().toLowerCase().replace(/_/g, '-');
}

export function callDisplayStatus(call) {
  const status = normalizeCallStatus(call?.status);
  return status || 'processing';
}

export function callSubject(call) {
  const displayStatus = callDisplayStatus(call);
  const currentSubject = String(call?.subject || '').trim();
  const lifecycleSubject = {
    queued: 'Incoming call',
    initiated: 'Incoming call',
    ringing: 'Incoming call',
    'in-progress': 'Call in progress',
    processing: 'Processing call',
    completed: 'Call completed',
    busy: 'Unanswered call',
    'no-answer': 'Unanswered call',
    canceled: 'Unanswered call',
    failed: 'Call failed',
  }[displayStatus] || 'Call in progress';
  if (!currentSubject || (
    currentSubject === 'Call completed' && displayStatus !== 'completed'
  )) return lifecycleSubject;
  return currentSubject;
}

function statusTone(status) {
  if (status === 'completed') return 'success';
  if (['failed', 'busy', 'no-answer', 'canceled'].includes(status)) return 'danger';
  return 'processing';
}

export function statusLabel(status) {
  return String(status || 'processing').replace(/-/g, ' ').replace(/^./, value => value.toUpperCase());
}

export default function CallHistoryItem({ call, onOpen, onDelete, onToggleRead }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  useEffect(() => {
    if (!menuOpen) return undefined;
    const close = event => {
      if (event.type === 'keydown' && event.key !== 'Escape') return;
      if (event.type === 'mousedown' && menuRef.current?.contains(event.target)) return;
      setMenuOpen(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', close);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', close);
    };
  }, [menuOpen]);

  const visibleOutcomes = (call.outcomes || []).filter((item, index, values) => (
    !(item.type === 'message_delivery' && ['skipped', 'not_attempted'].includes(item.status)) &&
    index === values.findIndex(candidate => candidate.type === item.type && candidate.status === item.status)
  )).slice(0, 3);
  const displayStatus = callDisplayStatus(call);
  const subject = callSubject(call);
  const assignment = call.messageAssignment;
  const isUnread = assignment?.assignedToYou && assignment.isRead === false;

  return (
    <article className={`call-history-row${isUnread ? ' unread' : ''}`}>
      <button className="call-history-main" type="button" onClick={() => onOpen(call)}>
        <span className="call-history-direction" aria-hidden="true">{isUnread ? '●' : '↗'}</span>
        <span className="call-history-copy">
          <strong>{subject}</strong>
          <small>{formatCallTime(call.startedAt)}</small>
          {call.followUp?.reminderDueAt ? <small className={new Date(call.followUp.reminderDueAt).getTime() <= Date.now() ? 'call-reminder-due' : ''}>{new Date(call.followUp.reminderDueAt).getTime() <= Date.now() ? 'Reminder due' : 'Your reminder'} · {formatCallTime(call.followUp.reminderDueAt)}</small> : null}
          {assignment || visibleOutcomes.length ? <span className="call-history-outcomes">
            {assignment ? <em className={`message-${assignment.kind}`}>{assignment.label}</em> : null}
            {call.messageHandling ? <em>{handlingLabel(call.messageHandling.status)}{call.messageHandling.memberName ? ` · ${call.messageHandling.memberName}` : ''}</em> : null}
            {visibleOutcomes.map((outcome, index) => <em className={outcome.status === 'failed' ? 'failed' : ''} key={`${outcome.type}-${index}`}>{outcome.label}</em>)}
          </span> : null}
        </span>
        <span className="call-history-facts">
          {call.recordingAvailable ? <span className="call-history-recorded">▶ Recording</span> : null}
          <span>{formatCallDuration(call.durationSeconds)}</span>
          <StatusBadge status={statusTone(displayStatus)}>{statusLabel(displayStatus)}</StatusBadge>
        </span>
      </button>
      <div ref={menuRef} className={`call-history-menu${menuOpen ? ' open' : ''}`}>
        <button className="call-history-menu-trigger" type="button" aria-label={`More actions for ${subject}`} aria-expanded={menuOpen} onClick={() => setMenuOpen(value => !value)}>⋯</button>
        {menuOpen ? <div className="call-history-menu-popover" role="menu">
          <button type="button" role="menuitem" onClick={() => { setMenuOpen(false); onOpen(call); }}>View call</button>
          {onToggleRead && assignment ? <button type="button" role="menuitem" onClick={() => { setMenuOpen(false); onToggleRead(call.id, !assignment.isRead); }}>{assignment.isRead ? 'Mark unread' : 'Mark read'}</button> : null}
          {onDelete ? <button className="danger" type="button" role="menuitem" onClick={() => { setMenuOpen(false); onDelete(call); }}>Delete call</button> : null}
        </div> : null}
      </div>
    </article>
  );
}

export function handlingLabel(status) {
  return { needs_attention: 'Needs attention', in_progress: 'In progress', handled: 'Handled' }[status] || 'Needs attention';
}
